import React, { useEffect, useState } from "react";
import api from "@/lib/api";
import { Users as UsersIcon } from "lucide-react";

function fmtINR(n) {
  if (n == null || isNaN(n)) return "—";
  return new Intl.NumberFormat("en-IN", { style: "currency", currency: "INR", maximumFractionDigits: 2 }).format(n);
}

/**
 * OPA status tiles for Task / Ticket detail headers.
 *
 * Behaviour by role:
 *   - Team Associate: tiles & summary show ONLY OPAs they raised.
 *   - Team Lead / Admin: tiles show every OPA on this task/ticket plus a
 *     per-user breakdown row ("How many times by how many users").
 *
 * Props: taskId? | issueId? | refreshKey
 */
export default function OPAStatusTiles({ taskId, issueId, refreshKey }) {
  const [summary, setSummary] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let aborted = false;
    if (!taskId && !issueId) return;
    setLoading(true);
    const params = {};
    if (taskId) params.task_id = taskId;
    if (issueId) params.issue_id = issueId;
    api
      .get("/opa-requests/summary", { params })
      .then(({ data }) => { if (!aborted) setSummary(data); })
      .catch(() => { if (!aborted) setSummary(null); })
      .finally(() => { if (!aborted) setLoading(false); });
    return () => { aborted = true; };
  }, [taskId, issueId, refreshKey]);

  const tiles = [
    {
      key: "requested",
      label: "Requested OPA",
      bar: "bg-amber-500/15 border-amber-500/20 text-amber-300",
      dot: "bg-amber-400",
    },
    {
      key: "approved",
      label: "Approved OPA",
      bar: "bg-sky-500/15 border-sky-500/20 text-sky-300",
      dot: "bg-sky-400",
    },
    {
      key: "received",
      label: "Received OPA",
      bar: "bg-emerald-500/15 border-emerald-500/20 text-emerald-300",
      dot: "bg-emerald-400",
    },
  ];

  const role = summary?.role;
  const scope = summary?.scope; // "own" | "all"
  const isLeadOrAdmin = role === "admin" || role === "team_lead";
  const byUser = summary?.by_user || [];
  const distinctUsers = summary?.distinct_users || 0;
  const totalCount = summary?.total_count || 0;
  const totalAmount = summary?.total_amount || 0;

  return (
    <section className="space-y-3" data-testid="opa-status-section">
      <div
        className="grid grid-cols-1 sm:grid-cols-3 gap-3"
        data-testid="opa-status-tiles"
      >
        {tiles.map((t) => {
          const s = summary?.[t.key];
          return (
            <div
              key={t.key}
              className={`rounded-lg border ${t.bar} p-4 flex items-start justify-between`}
              data-testid={`opa-tile-${t.key}`}
            >
              <div>
                <div className="text-[11px] uppercase tracking-wider font-medium flex items-center gap-1.5">
                  <span className={`w-1.5 h-1.5 rounded-full ${t.dot}`} />
                  {t.label}
                </div>
                <div className="text-2xl font-semibold font-mono mt-1 text-white">
                  {loading ? "—" : fmtINR(s?.total || 0)}
                </div>
                <div className="text-[11px] text-zinc-400 mt-0.5">
                  {loading ? "" : `${s?.count || 0} request${(s?.count || 0) === 1 ? "" : "s"}`}
                </div>
              </div>
            </div>
          );
        })}
      </div>

      {/* Scope indicator for associates */}
      {!loading && scope === "own" && (
        <div className="text-[11px] text-zinc-500" data-testid="opa-scope-own">
          Showing only OPAs raised by you. Team Leads and Admin can see the full
          picture across all users on this item.
        </div>
      )}

      {/* Detailed view for Team Leads & Admin */}
      {!loading && isLeadOrAdmin && totalCount > 0 && (
        <div
          className="bg-zinc-900 border border-white/10 rounded-lg overflow-hidden"
          data-testid="opa-by-user"
        >
          <div className="px-4 py-3 border-b border-white/10 flex items-center justify-between flex-wrap gap-2">
            <div className="flex items-center gap-2 text-[11px] uppercase tracking-wider text-zinc-500 font-medium">
              <UsersIcon className="w-3.5 h-3.5" />
              OPA Raised — Per User Breakdown
            </div>
            <div className="text-xs text-zinc-400">
              <span className="text-white font-medium" data-testid="opa-total-count">{totalCount}</span>{" "}
              request{totalCount === 1 ? "" : "s"} ·{" "}
              <span className="text-white font-medium" data-testid="opa-distinct-users">{distinctUsers}</span>{" "}
              user{distinctUsers === 1 ? "" : "s"} ·{" "}
              <span className="text-amber-300 font-mono" data-testid="opa-total-amount">{fmtINR(totalAmount)}</span>{" "}
              total
            </div>
          </div>
          <table className="w-full text-sm">
            <thead>
              <tr className="bg-zinc-950/50 border-b border-white/10 text-left">
                <th className="px-4 py-2.5 text-[11px] uppercase tracking-wider text-zinc-500 font-medium">User</th>
                <th className="px-4 py-2.5 text-[11px] uppercase tracking-wider text-zinc-500 font-medium">OPAs Raised</th>
                <th className="px-4 py-2.5 text-[11px] uppercase tracking-wider text-zinc-500 font-medium">Total Amount</th>
                <th className="px-4 py-2.5 text-[11px] uppercase tracking-wider text-zinc-500 font-medium">Approved Amount</th>
              </tr>
            </thead>
            <tbody>
              {byUser.map((u) => (
                <tr key={u.user_id} className="border-b border-white/5" data-testid={`opa-user-row-${u.user_id}`}>
                  <td className="px-4 py-2.5 text-zinc-200">{u.user_name}</td>
                  <td className="px-4 py-2.5 text-zinc-300 font-mono">{u.count}</td>
                  <td className="px-4 py-2.5 text-amber-300 font-mono">{fmtINR(u.total)}</td>
                  <td className="px-4 py-2.5 text-sky-300 font-mono">{fmtINR(u.approved_total)}</td>
                </tr>
              ))}
            </tbody>
            <tfoot>
              <tr className="bg-zinc-950/30">
                <td className="px-4 py-2.5 text-[11px] uppercase tracking-wider text-zinc-500 font-medium">Total</td>
                <td className="px-4 py-2.5 text-zinc-100 font-mono font-semibold">{totalCount}</td>
                <td className="px-4 py-2.5 text-amber-300 font-mono font-semibold">{fmtINR(totalAmount)}</td>
                <td className="px-4 py-2.5 text-sky-300 font-mono font-semibold">
                  {fmtINR(byUser.reduce((a, u) => a + (u.approved_total || 0), 0))}
                </td>
              </tr>
            </tfoot>
          </table>
        </div>
      )}
    </section>
  );
}
